import { useEffect, useState, useCallback } from 'preact/hooks'
import { lookupsApi } from '@/api'
import type { EmailRuleLookupResponse } from '@/types/lookups'

const cache = new Map<string, EmailRuleLookupResponse>()
const inFlight = new Map<string, Promise<EmailRuleLookupResponse | null>>()

export interface UseEmailRuleLookupResult {
  data: EmailRuleLookupResponse | null
  isLoading: boolean
  error: string | null
  refetch: () => void
}

function cacheKey(workspaceId: string, webId: string): string {
  return `${workspaceId}::${webId}`
}

export function useEmailRuleLookup(
  workspaceId: string | undefined,
  webId: string | undefined
): UseEmailRuleLookupResult {
  const key = workspaceId && webId ? cacheKey(workspaceId, webId) : null
  const [data, setData] = useState<EmailRuleLookupResponse | null>(key ? cache.get(key) ?? null : null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchRules = useCallback(async (force = false) => {
    if (!workspaceId || !webId) {
      setData(null)
      return
    }
    const k = cacheKey(workspaceId, webId)

    if (!force && cache.has(k)) {
      setData(cache.get(k) ?? null)
      return
    }

    const existing = inFlight.get(k)
    if (existing && !force) {
      const result = await existing
      if (result) setData(result)
      return
    }

    setIsLoading(true)
    setError(null)

    const promise = lookupsApi.getEmailRules(workspaceId, webId).then(response => {
      if (response.error || !response.data) {
        setError(response.error || 'Failed to load email rule lookup')
        return null
      }
      cache.set(k, response.data)
      return response.data
    }).finally(() => {
      inFlight.delete(k)
      setIsLoading(false)
    })

    inFlight.set(k, promise)
    const result = await promise
    if (result) setData(result)
  }, [workspaceId, webId])

  useEffect(() => {
    void fetchRules()
  }, [fetchRules])

  const refetch = useCallback(() => {
    if (key) cache.delete(key)
    void fetchRules(true)
  }, [key, fetchRules])

  return { data, isLoading, error, refetch }
}
